import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ADD_ONS } from '../data/constants';

const SERVICE_STEPS = {
  // The Advanced one-time package (ONE_TIME_PACKAGES id 'interior') — most
  // one_time bookings are this, not the maintenance-program Deep Clean/Reset
  // baseline visit, so it needs its own entry instead of falling through to
  // those. Mirrors ONE_TIME_PACKAGES.interior.features.
  'Interior Detail': [
    { icon: '🌀', title: 'Full Vacuum', desc: 'Seats, carpet, floor mats & trunk.' },
    { icon: '🧽', title: 'Dashboard, Door Panels & Console Wipe-Down', desc: 'Every interior plastic and trim surface wiped clean.' },
    { icon: '🥤', title: 'Cracks, Crevices & Cup Holder Cleaning', desc: 'The tight spots a standard clean skips.' },
    { icon: '🧴', title: 'Light Surface Stain Treatment', desc: 'Spot treatment for light staining on seats and carpet.' },
    { icon: '🪟', title: 'Interior Glass', desc: 'All interior windows cleaned streak-free.' },
    { icon: '✨', title: 'UV Protectant', desc: 'Dash and plastics protected against sun damage and fading.' },
    { icon: '🚪', title: 'Door & Trunk Jamb Wipe-Down', desc: 'Jambs cleaned, finishing the detail edge to edge.' },
  ],
  // 'Average' one-time package (id maintenance_refresh) — mirrors its features.
  'Average': [
    { icon: '🌀', title: 'Vacuum', desc: 'Seats, carpet & floor mats.' },
    { icon: '🧽', title: 'Dashboard, Door Panels & Console Wipe-Down', desc: 'Every interior plastic and trim surface wiped clean.' },
    { icon: '🪟', title: 'Interior Glass', desc: 'All interior windows cleaned streak-free.' },
  ],
  // 'The Alchemist' one-time package — Advanced plus steam & deep shampoo.
  'The Alchemist': [
    { icon: '🌀', title: 'Full Vacuum', desc: 'Seats, carpet, floor mats & trunk.' },
    { icon: '💨', title: 'Steam Treatment', desc: 'High-heat steam sanitizes seats, carpet, vents & door panels.' },
    { icon: '💧', title: 'Carpet & Seat Deep Shampoo', desc: 'Machine extraction lifts embedded dirt and set-in grime.' },
    { icon: '🧽', title: 'Dashboard, Door Panels & Console Wipe-Down', desc: 'Every interior plastic and trim surface wiped clean.' },
    { icon: '🥤', title: 'Cracks, Crevices & Cup Holder Cleaning', desc: 'The tight spots a standard clean skips.' },
    { icon: '🧴', title: 'Light Surface Stain Treatment', desc: 'Spot treatment for light staining on seats and carpet.' },
    { icon: '🪟', title: 'Interior Glass', desc: 'All interior windows cleaned streak-free.' },
    { icon: '✨', title: 'UV Protectant', desc: 'Dash and plastics protected against sun damage and fading.' },
    { icon: '🚪', title: 'Door & Trunk Jamb Wipe-Down', desc: 'Jambs cleaned, finishing the detail edge to edge.' },
  ],
  'Alchemist Deep Clean': [
    { icon: '🌀', title: 'Full Vacuum & Debris Removal', desc: 'Every surface, crevice, and corner — seats, floors, trunk, door pockets, under seats.' },
    { icon: '🔥', title: 'Hot Water Extraction', desc: 'Industrial-grade deep extraction lifts embedded dirt, bacteria, and stains from fabric at the root.' },
    { icon: '💨', title: 'Full Steam Clean', desc: 'Dashboard, vents, door panels, console, and trim — steam-sanitized to eliminate grime without harsh chemicals.' },
    { icon: '🌬️', title: 'Air Circulation Reset', desc: 'Bio-bomb odor neutralization + vents flushed. The air inside your car is treated from the source, not masked.' },
    { icon: '✨', title: 'Surface Treatment & UV Protection', desc: 'Every interior plastic and trim surface dressed and protected against sun damage and fading.' },
    { icon: '🧴', title: 'Leather & Upholstery Care', desc: 'Leather cleaned, conditioned, and protected. Fabric treated to repel future staining.' },
  ],
  'Alchemist Reset': [
    { icon: '🌀', title: 'Full Vacuum & Debris Removal', desc: 'Every surface, crevice, and corner — seats, floors, trunk, door pockets, under seats.' },
    { icon: '🔥', title: 'Hot Water Extraction', desc: 'Industrial-grade deep extraction lifts embedded dirt, bacteria, and stains from fabric at the root.' },
    { icon: '💨', title: 'Full Steam Clean', desc: 'Dashboard, vents, door panels, console, and trim — steam-sanitized to eliminate grime without harsh chemicals.' },
    { icon: '🌬️', title: 'Air Circulation Reset', desc: 'Bio-bomb odor neutralization + vents flushed. The air inside your car is treated from the source, not masked.' },
    { icon: '✨', title: 'Surface Treatment & UV Protection', desc: 'Every interior plastic and trim surface dressed and protected against sun damage and fading.' },
  ],
  'Monthly Maintenance': [
    { icon: '🌀', title: 'Full Vacuum & Wipe-Down', desc: 'Complete vacuum of all surfaces, floors, and seats. Every panel wiped clean.' },
    { icon: '🪟', title: 'Interior Glass', desc: 'All windows and mirrors cleaned streak-free for maximum clarity.' },
    { icon: '💨', title: 'Spot Steam', desc: 'Any new stains or buildup hit with targeted steam treatment before they set.' },
    { icon: '🌿', title: 'Deodorize & Refresh', desc: 'Light deodorize treatment keeps the interior smelling clean between sessions.' },
  ],
};

function getSteps(serviceName) {
  if (!serviceName) return [];
  const lower = serviceName.toLowerCase();
  if (lower === 'the alchemist') return SERVICE_STEPS['The Alchemist'];
  if (lower === 'average') return SERVICE_STEPS['Average'];
  if (lower === 'advanced') return SERVICE_STEPS['Interior Detail'];
  if (lower.includes('deep clean') || lower.includes('alchemist deep')) return SERVICE_STEPS['Alchemist Deep Clean'];
  if (lower.includes('reset') || lower.includes('alchemist reset')) return SERVICE_STEPS['Alchemist Reset'];
  if (lower.includes('maintenance') || lower.includes('monthly')) return SERVICE_STEPS['Monthly Maintenance'];
  if (lower.includes('interior')) return SERVICE_STEPS['Interior Detail'];
  // Interior Detail is the actual standalone one-time package — safer
  // default than the maintenance-program Deep Clean list, which includes
  // steps (steam, odor bio-bomb) that aren't part of a plain one-time detail.
  return SERVICE_STEPS['Interior Detail'];
}

// Add-ons are only "what was done" if the client actually paid for and
// received them — pulled from the ADD_ONS catalog so labels/icons/desc
// stay in sync with the one place they're defined.
function getAddOnSteps(addOns) {
  if (!addOns || addOns.length === 0) return [];
  const purchasedLabels = new Set(addOns.map(a => a.label));
  return ADD_ONS
    .filter(a => purchasedLabels.has(a.label))
    .map(a => ({ icon: a.emoji, title: a.label, desc: a.desc }));
}

const TIP_OPTIONS = [
  { label: '10%', pct: 0.10 },
  { label: '15%', pct: 0.15 },
  { label: '20%', pct: 0.20 },
  { label: 'None', pct: 0 },
];

export default function InvoicePayPage() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTip, setSelectedTip] = useState('none');
  const [customTip, setCustomTip] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    fetch(`/api/invoice-pay?bookingId=${bookingId}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const getTipAmount = useCallback(() => {
    if (selectedTip === 'custom') {
      return parseFloat(customTip) || 0;
    }
    if (selectedTip === 'none') return 0;
    const opt = TIP_OPTIONS.find(o => o.label === selectedTip);
    if (!opt || opt.pct === 0) return 0;
    return Math.round((data?.invoiceTotal || 0) * opt.pct * 100) / 100;
  }, [selectedTip, customTip, data]);

  const tipAmount = getTipAmount();
  const totalDue = (data?.invoiceTotal || 0) + tipAmount;

  const handlePay = async () => {
    if (!data) return;
    setPaying(true);
    try {
      if (tipAmount > 0) {
        const res = await fetch('/api/invoice-pay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookingId, tipAmount }),
        });
        const json = await res.json();
        if (json.url) {
          window.location.href = json.url;
        } else {
          alert(json.error || 'Something went wrong. Please try again.');
          setPaying(false);
        }
      } else {
        window.location.href = data.invoiceUrl;
      }
    } catch {
      alert('Something went wrong. Please try again.');
      setPaying(false);
    }
  };

  const steps = data
    ? [...getSteps(data.serviceName), ...getAddOnSteps(data.addOns)]
        .filter(s => !(data.excludedSteps || []).includes(s.title))
    : [];
  const isFreeInvoice = (data?.invoiceTotal || 0) === 0;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #1a1a1a', borderTopColor: '#68caf1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#555', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase' }}>Loading invoice…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <p style={{ color: '#68caf1', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Monarch Detailing</p>
          <p style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Invoice not found</p>
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>{error}</p>
        </div>
      </div>
    );
  }

  // A $0 (free/comped) invoice gets auto-marked "paid" by Stripe the instant
  // it's finalized — nothing was ever actually paid, so don't short-circuit
  // past the tip section for it like a real paid invoice.
  if (data?.invoiceStatus === 'paid' && (data.invoiceTotal || 0) > 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
        <Header />
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '48px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>✓</div>
          <p style={{ color: '#4ade80', fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Already Paid</p>
          <h2 style={{ color: '#fff', fontSize: 26, fontWeight: 700, marginBottom: 12 }}>This invoice has been paid.</h2>
          <p style={{ color: '#aaa', fontSize: 15, lineHeight: 1.6 }}>Thank you, {data.firstName}. Your payment has been received. We appreciate you choosing Monarch Detailing.</p>
          <GoogleReviewBtn style={{ marginTop: 32 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
      <Header />

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 16px 60px' }}>

        {/* Greeting */}
        <div style={{ textAlign: 'center', padding: '40px 0 32px' }}>
          <p style={{ color: '#68caf1', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', margin: '0 0 12px' }}>Invoice</p>
          <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 700, margin: '0 0 10px', lineHeight: 1.2 }}>
            Your invoice is ready,<br />{data.firstName}.
          </h1>
          {data.scheduledDate && (
            <p style={{ color: '#555', fontSize: 13, margin: 0 }}>Service date: {data.scheduledDate}</p>
          )}
        </div>

        {/* Invoice Summary Card */}
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: 12, padding: '24px', marginBottom: 20 }}>
          <p style={{ color: '#68caf1', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid #222' }}>Invoice Summary</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {data.vehicleStr && (
                <tr>
                  <td style={tdLabel}>Vehicle</td>
                  <td style={tdValue}>{data.vehicleStr}{data.color ? ` · ${data.color}` : ''}</td>
                </tr>
              )}
              {data.address && (
                <tr>
                  <td style={tdLabel}>Location</td>
                  <td style={tdValue}>{data.address}</td>
                </tr>
              )}
              <tr>
                <td style={tdLabel}>Service</td>
                <td style={tdValue}>{data.serviceName}</td>
              </tr>
              {data.addOns && data.addOns.length > 0 && data.addOns.map((a, i) => (
                <tr key={i}>
                  <td style={tdLabel}>{a.label}</td>
                  <td style={tdValue}>${a.price.toFixed(2)}</td>
                </tr>
              ))}
              {data.depositCredit > 0 && (
                <tr>
                  <td style={{ ...tdLabel, color: '#4ade80' }}>Deposit Credit</td>
                  <td style={{ ...tdValue, color: '#4ade80' }}>−${data.depositCredit.toFixed(2)}</td>
                </tr>
              )}
              <tr>
                <td style={{ color: '#68caf1', fontSize: 14, fontWeight: 700, paddingTop: 14 }}>Amount Due</td>
                <td style={{ color: '#68caf1', fontSize: 18, fontWeight: 700, textAlign: 'right', paddingTop: 14 }}>${(data.invoiceTotal || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* What Was Done */}
        {steps.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ color: '#68caf1', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 14px' }}>What Was Done to Your Vehicle</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {steps.map((step, i) => (
                <div key={i} style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 10, padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1 }}>{step.icon}</span>
                  <div>
                    <p style={{ color: '#fff', fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>{step.title}</p>
                    <p style={{ color: '#666', fontSize: 13, margin: 0, lineHeight: 1.5 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tip Section */}
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: 12, padding: '24px', marginBottom: 20 }}>
          <p style={{ color: '#68caf1', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 6px' }}>Add a Gratuity</p>
          <p style={{ color: '#555', fontSize: 13, margin: '0 0 18px' }}>
            {isFreeInvoice ? 'This one was on the house — any tip goes straight to Akeem!' : '100% goes directly to Akeem.'}
          </p>
          {/* Percentage buttons are meaningless on a $0 invoice (all compute to $0) —
              skip straight to the custom amount for free/comped details. */}
          {!isFreeInvoice && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
            {TIP_OPTIONS.map(opt => {
              const isActive = opt.pct === 0 ? selectedTip === 'none' : selectedTip === opt.label;
              const dollarAmt = opt.pct > 0 ? `$${(Math.round((data?.invoiceTotal || 0) * opt.pct * 100) / 100).toFixed(2)}` : '';
              return (
                <button
                  key={opt.label}
                  onClick={() => { setSelectedTip(opt.pct === 0 ? 'none' : opt.label); setCustomTip(''); }}
                  style={{
                    background: isActive ? '#68caf1' : '#0a0a0a',
                    color: isActive ? '#000' : '#aaa',
                    border: `1px solid ${isActive ? '#68caf1' : '#222'}`,
                    borderRadius: 8,
                    padding: '10px 4px',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                  {dollarAmt && <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>{dollarAmt}</div>}
                </button>
              );
            })}
          </div>}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: customTip ? '#fff' : '#444', fontSize: 15, pointerEvents: 'none' }}>$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Custom amount"
              value={customTip}
              onChange={e => { setCustomTip(e.target.value); setSelectedTip('custom'); }}
              onFocus={() => setSelectedTip('custom')}
              style={{
                width: '100%',
                background: selectedTip === 'custom' ? '#0a0a0a' : '#0a0a0a',
                border: `1px solid ${selectedTip === 'custom' ? '#68caf1' : '#222'}`,
                borderRadius: 8,
                padding: '11px 14px 11px 28px',
                color: '#fff',
                fontSize: 15,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Total Breakdown */}
        <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#666', fontSize: 14 }}>Invoice total</span>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>${(data?.invoiceTotal || 0).toFixed(2)}</span>
          </div>
          {tipAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#4ade80', fontSize: 14 }}>Gratuity</span>
              <span style={{ color: '#4ade80', fontSize: 14, fontWeight: 600 }}>+${tipAmount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid #222', paddingTop: 12, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#68caf1', fontSize: 16, fontWeight: 700 }}>You pay</span>
            <span style={{ color: '#68caf1', fontSize: 20, fontWeight: 700 }}>${totalDue.toFixed(2)}</span>
          </div>
        </div>

        {/* Pay Button */}
        <button
          onClick={handlePay}
          disabled={paying}
          style={{
            width: '100%',
            background: paying ? '#3a7a91' : '#68caf1',
            color: '#000',
            fontWeight: 700,
            fontSize: 16,
            padding: '16px',
            borderRadius: 10,
            border: 'none',
            cursor: paying ? 'not-allowed' : 'pointer',
            letterSpacing: 0.5,
            marginBottom: 12,
            transition: 'background 0.15s',
          }}
        >
          {paying ? 'Redirecting to Stripe…' : `Pay Now — $${totalDue.toFixed(2)} →`}
        </button>

        <p style={{ textAlign: 'center', color: '#333', fontSize: 12, margin: '0 0 48px' }}>
          🔒 Secured by Stripe. Monarch Detailing never stores your card information.
        </p>

        {/* Google Review */}
        <GoogleReviewBtn />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{ background: '#000', borderBottom: '1px solid #1a1a1a', padding: '28px 20px', textAlign: 'center' }}>
      <img
        src="/images/logo.png"
        alt="Monarch Detailing"
        style={{ height: 64, width: 'auto', objectFit: 'contain', marginBottom: 12, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <p style={{ color: '#68caf1', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', margin: '0 0 4px' }}>Monarch Detailing</p>
      <p style={{ color: '#333', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: 0 }}>Automotive Alchemy</p>
    </div>
  );
}

function GoogleReviewBtn() {
  return (
    <div style={{ background: '#0f0f0f', border: '1px solid #1a1a1a', borderRadius: 12, padding: '24px', textAlign: 'center' }}>
      <p style={{ color: '#68caf1', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 8px' }}>Enjoyed the service?</p>
      <p style={{ color: '#666', fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>A quick Google review helps Akeem grow Monarch Detailing. It takes 30 seconds and means everything.</p>
      <a
        href="https://g.page/r/CXdr5n1W7IbjEBM/review"
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'inline-block', background: '#fff', color: '#000', fontWeight: 700, fontSize: 13, padding: '11px 24px', borderRadius: 8, textDecoration: 'none' }}
      >
        ⭐ Leave a Google Review
      </a>
    </div>
  );
}

const tdLabel = { color: '#666', fontSize: 13, padding: '9px 0', borderBottom: '1px solid #1a1a1a', verticalAlign: 'top' };
const tdValue = { color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'right', padding: '9px 0', borderBottom: '1px solid #1a1a1a', verticalAlign: 'top' };
